/**
 * CYDEcore™ design tokens — mirrors `:root` from program-management HTML mocks
 * (e.g. Aegis `docs/program-management/program-management-ui/_base.css`).
 *
 * - **Theme preset:** merged into `document.documentElement` when the user selects theme `cydecore`.
 * - **Subtree scope:** spread `CYDECORE_SCOPE_STYLE` onto any host `style` so embedded shells resolve `var(--cydecore-*)` without changing global theme.
 * - **Modals:** Radix portals render under `body`; use `<DialogContent variant="cydecore" />` or `<AlertDialogContent variant="cydecore" />` so scope style is inlined on the content node (see `components/ui/dialog.tsx`, `alert-dialog.tsx`, `.cydecore-dialog-overlay` in `app/globals.css`).
 */
export const CYDECORE_CSS_VAR_MAP: Record<string, string> = {
  // Page bg: #0b0e17 = rgb(11, 14, 23). Mock catalog uses ~rgb(11, 14, 22).
  "--cydecore-bg": "#0b0e17",
  // Card surface: ~5 units lifted off page bg, matching the mock (#10141f ≈ rgb(16, 20, 31)).
  // Contrast is generated by this lift PLUS the alpha-white hairline border + top-edge rail,
  // not by big absolute lift — that visually overshoots and breaks parity with mocks.
  "--cydecore-bg2": "rgb(15, 20, 32)",
  "--cydecore-surface": "rgb(20, 25, 41)",
  "--cydecore-surface2": "rgb(26, 32, 53)",
  "--cydecore-surface3": "rgb(31, 38, 64)",
  "--cydecore-border": "rgba(255,255,255,0.07)",
  "--cydecore-border2": "rgba(255,255,255,0.13)",
  // Sticky header overlay color (translucent so the page glow shows through).
  // Use these instead of hard-coding rgba literals in components.
  "--cydecore-header-bg": "rgba(11, 14, 23, 0.92)",
  "--cydecore-header-bg-supports": "rgba(11, 14, 23, 0.85)",
  "--cydecore-text": "#f0f2f7",
  "--cydecore-text2": "#9ba3b8",
  "--cydecore-text3": "#4f5873",
  "--cydecore-blue": "#3b7cf4",
  "--cydecore-blue-lt": "#6699ff",
  "--cydecore-blue-hover": "#4d8cf5",
  "--cydecore-blue-dim": "rgba(59,124,244,0.10)",
  "--cydecore-blue-border": "rgba(59,124,244,0.25)",
  "--cydecore-green": "#2dd4a0",
  "--cydecore-green-dim": "rgba(45,212,160,0.08)",
  "--cydecore-green-b": "rgba(45,212,160,0.20)",
  "--cydecore-amber": "#f5a623",
  "--cydecore-amber-dim": "rgba(245,166,35,0.08)",
  "--cydecore-amber-b": "rgba(245,166,35,0.22)",
  "--cydecore-red": "#f05252",
  "--cydecore-violet": "#8b79f0",
  "--cydecore-violet-dim": "rgba(139,121,240,0.10)",
  "--cydecore-d-rs": "#f0713a",
  "--cydecore-d-rc": "#e05b9b",
  "--cydecore-progress-from": "#1a5fd4",
  /** Breadcrumb / link hover (mock-adjacent lighter blue) */
  "--cydecore-link-hover": "#99b8ff",
  /** Small maturity / heat gradients */
  "--cydecore-amber-deep": "#c07d10",
  "--cydecore-green-deep": "#1ab082",
}

/**
 * Light counterpart for CYDEcore tokens. Mirrors the same variable names so any
 * component referencing `var(--cydecore-*)` still resolves when the global theme is light.
 * Values intentionally match the slate-50 / slate-200 / slate-900 family used elsewhere
 * by light shadcn defaults so the Command Center stops looking dark when the user toggles
 * to light. Subjective polish (hue/contrast) can be tuned without touching consumers.
 */
export const CYDECORE_CSS_VAR_MAP_LIGHT: Record<string, string> = {
  // Light mirror: page is slate-50, cards are pure white (clear lift over page).
  "--cydecore-bg": "rgb(241, 245, 249)",
  "--cydecore-bg2": "rgb(255, 255, 255)",
  "--cydecore-surface": "rgb(248, 250, 252)",
  "--cydecore-surface2": "rgb(241, 245, 249)",
  "--cydecore-surface3": "rgb(226, 232, 240)",
  "--cydecore-border": "rgba(15, 23, 42, 0.08)",
  "--cydecore-border2": "rgba(15, 23, 42, 0.14)",
  // Sticky header overlay (translucent white over the light page glow).
  "--cydecore-header-bg": "rgba(255, 255, 255, 0.92)",
  "--cydecore-header-bg-supports": "rgba(255, 255, 255, 0.85)",
  "--cydecore-text": "rgb(15, 23, 42)",
  "--cydecore-text2": "rgb(71, 85, 105)",
  "--cydecore-text3": "rgb(148, 163, 184)",
  "--cydecore-blue": "#2563eb",
  "--cydecore-blue-lt": "#3b82f6",
  "--cydecore-blue-hover": "#1d4ed8",
  "--cydecore-blue-dim": "rgba(37, 99, 235, 0.08)",
  "--cydecore-blue-border": "rgba(37, 99, 235, 0.20)",
  "--cydecore-green": "#059669",
  "--cydecore-green-dim": "rgba(5, 150, 105, 0.08)",
  "--cydecore-green-b": "rgba(5, 150, 105, 0.20)",
  "--cydecore-amber": "#b45309",
  "--cydecore-amber-dim": "rgba(180, 83, 9, 0.10)",
  "--cydecore-amber-b": "rgba(180, 83, 9, 0.22)",
  "--cydecore-red": "#dc2626",
  "--cydecore-violet": "#7c3aed",
  "--cydecore-violet-dim": "rgba(124, 58, 237, 0.10)",
  "--cydecore-d-rs": "#ea580c",
  "--cydecore-d-rc": "#db2777",
  "--cydecore-progress-from": "#1d4ed8",
  "--cydecore-link-hover": "#1d4ed8",
  "--cydecore-amber-deep": "#92400e",
  "--cydecore-green-deep": "#047857",
}

/**
 * Frozen copy for subtree theming. Spread onto a host, e.g.
 * `style={{ maxHeight: "...", ...CYDECORE_SCOPE_STYLE }}` — custom properties inherit to descendants.
 */
export const CYDECORE_SCOPE_STYLE = Object.freeze({
  ...CYDECORE_CSS_VAR_MAP,
}) as Readonly<Record<string, string>>

/** Light-theme equivalent. Use whenever the global theme category is `light`. */
export const CYDECORE_SCOPE_STYLE_LIGHT = Object.freeze({
  ...CYDECORE_CSS_VAR_MAP_LIGHT,
}) as Readonly<Record<string, string>>

/**
 * Pick the right CYDEcore scope for the current global theme. Pass the resolved
 * theme category (`'dark' | 'light'`) — falls back to dark when unknown.
 */
export function getCydecoreScopeStyle(category: "dark" | "light" | undefined | null) {
  return category === "light" ? CYDECORE_SCOPE_STYLE_LIGHT : CYDECORE_SCOPE_STYLE
}

/** Use with shadcn `DialogContent`: `variant={CYDECORE_DIALOG_CONTENT_VARIANT}` — applies overlay + scoped CSS vars (portals do not inherit Command Center shell). */
export const CYDECORE_DIALOG_CONTENT_VARIANT = "cydecore" as const
